import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection,
  doc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Bell, Mail } from "lucide-react";
import { db } from "../../lib/firebase";
import { useFirestoreDocOnce } from "../../hooks/useFirestoreDocOnce";
import { useEvent } from "../../layouts/EventLayout";
import { PageHeader } from "../../components/PageHeader";
import type {
  Audience,
  Notification,
  Person,
  TimestampLike,
} from "@event-planner/shared";
import { useFirestoreCollectionOnce } from "../../hooks/useFirestoreCollectionOnce";
import { useEventParticipants } from "../../hooks/useEventParticipants";
import { useIsPhone } from "../../hooks/useIsPhone";
import {
  formatAudienceLabel,
  type AudienceLabelUser,
} from "@event-planner/shared";

type EmailDeliveryStatus = "queued" | "sent" | "delivered" | "failed" | "bounced";

interface EmailStats {
  total: number;
  byStatus: Record<EmailDeliveryStatus, number>;
}

interface ReceiptStats {
  total: number;
  read: number;
  unread: number;
}

const EMPTY_EMAIL_STATS: EmailStats = {
  total: 0,
  byStatus: {
    queued: 0,
    sent: 0,
    delivered: 0,
    failed: 0,
    bounced: 0,
  },
};

const EMPTY_RECEIPT_STATS: ReceiptStats = { total: 0, read: 0, unread: 0 };

/** Phone: delivery stats as an even two-column grid instead of a ragged wrap. */
const PHONE_STAT_GRID = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 1,
} as const;

function formatTimestamp(ts: TimestampLike | null, timezone: string): string {
  if (!ts) return "—";
  const date = ts.toDate();
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(date);
}

function formatAudience(
  n: Notification,
  audienceById: Map<string, Audience>,
  userById: Map<string, AudienceLabelUser>,
): string {
  if (!n.audienceId) return "(no audience)";
  const audience = audienceById.get(n.audienceId);
  return formatAudienceLabel(audience, audienceById, userById);
}

export function NotificationDetailPage() {
  const isPhone = useIsPhone();
  const { eventId, notificationId } = useParams<{
    eventId: string;
    notificationId: string;
  }>();
  const navigate = useNavigate();
  const { event } = useEvent();

  const notificationRef = useMemo(
    () => (notificationId ? doc(db, "notifications", notificationId) : null),
    [notificationId],
  );
  const {
    data: notification,
    loading: loadingNotification,
    refetch: refetchNotification,
  } = useFirestoreDocOnce<Notification>(notificationRef);

  const [emailStats, setEmailStats] = useState<EmailStats | null>(null);
  const [loadingEmailStats, setLoadingEmailStats] = useState(false);

  const [receiptStats, setReceiptStats] = useState<ReceiptStats | null>(null);
  const [loadingReceiptStats, setLoadingReceiptStats] = useState(false);
  // Surface receipt query errors instead of silently rendering zeros.
  // Without this, a permission_denied or transient network failure on the
  // receipts query produced "0 opened / 0 unread" against a 5-recipient
  // notification, indistinguishable from "no one has opened it yet".
  const [receiptStatsError, setReceiptStatsError] = useState<string | null>(null);

  // Incremented on each poll tick while waiting for the CF to finish
  // fan-out. Used as an effect dependency so the receipt + email queries
  // re-run alongside the notification doc refetch.
  const [pollNonce, setPollNonce] = useState(0);

  // Resolve audienceId → audience doc for the Audience row. Includes
  // anonymous compositions so the formatter can render their includes.
  const audiencesQuery = useMemo(
    () =>
      eventId
        ? query(
            collection(db, "audiences"),
            where("eventId", "==", eventId),
          )
        : null,
    [eventId],
  );
  const { data: audiences } =
    useFirestoreCollectionOnce<Audience>(audiencesQuery);
  // Include pending participants so the pending-recipient breakdown can
  // resolve names — audience-picker surfaces use the same shape per ADR-136.
  const { persons: eventMembers } = useEventParticipants(eventId, [
    "active",
    "pending",
  ]);
  const audienceById = useMemo(
    () => new Map(audiences.map((a) => [a.id, a])),
    [audiences],
  );
  const userById = useMemo<Map<string, AudienceLabelUser>>(
    () =>
      new Map(
        eventMembers.map((p) => [p.id, { id: p.id, displayName: p.displayName }]),
      ),
    [eventMembers],
  );

  // Recipient breakdown for this notification's audience (ADR-137).
  //   activeRecipientCount  = persons in resolvedPersonIds with authUid != null
  //   pendingRecipients     = persons in resolvedPersonIds with authUid === null
  // Pending people get an inbox receipt at activation time via
  // `backfillSentNotificationReceiptsForActivatedPerson` (ADR-136 amendment to
  // activateAccount). The audience cache itself no longer projects to uids;
  // the active/pending split is derived client-side via the persons join.
  const notificationAudience = useMemo(
    () =>
      notification?.audienceId
        ? (audienceById.get(notification.audienceId) ?? null)
        : null,
    [audienceById, notification?.audienceId],
  );
  const activeRecipientCount = useMemo(() => {
    if (!notificationAudience) return 0;
    const resolvedPersonIds = new Set(notificationAudience.resolvedPersonIds ?? []);
    return eventMembers.filter(
      (p) => p.authUid != null && resolvedPersonIds.has(p.id),
    ).length;
  }, [eventMembers, notificationAudience]);
  const pendingRecipients = useMemo<Person[]>(() => {
    if (!notificationAudience) return [];
    const resolvedPersonIds = new Set(notificationAudience.resolvedPersonIds ?? []);
    return eventMembers.filter(
      (p) => p.authUid === null && resolvedPersonIds.has(p.id),
    );
  }, [eventMembers, notificationAudience]);

  const timezone = event?.timezone ?? "UTC";
  const includesEmail = notification?.channels.includes("email") ?? false;
  const includesPush = notification?.channels.includes("push") ?? false;
  const isSent = notification?.status === "sent";
  // "Delivering" = doc has status='sent' but the CF hasn't yet written
  // `sentAt` + counters. That window is typically sub-second on the
  // emulator and couple of seconds in production.
  const isDelivering = isSent && notification?.sentAt === null;

  // Poll the notification doc + sub-queries while the CF is still fanning
  // out. The detail page is the landing screen after the admin hits "Send
  // now" — when the user arrives, `sentAt` is null and the stats panels
  // are empty. We poll every 400ms for up to 30 seconds, refetching the
  // notification doc and bumping `pollNonce` so the receipt + email
  // effects re-run too. The effect unwinds itself as soon as `sentAt`
  // becomes non-null.
  //
  // Why 30s: the CF fan-out batches receipt + mail writes per recipient,
  // and on cold-start emulator runs (or first deploy after deploy) it can
  // take 10–20s for the trigger queue to land. An 8s ceiling left the page
  // stuck in "Delivering…" forever even though the send succeeded server-
  // side.
  useEffect(() => {
    if (!isDelivering) return;

    const startTime = Date.now();
    const POLL_INTERVAL_MS = 400;
    const POLL_TIMEOUT_MS = 30000;

    const timer = setInterval(() => {
      if (Date.now() - startTime > POLL_TIMEOUT_MS) {
        clearInterval(timer);
        return;
      }
      refetchNotification();
      setPollNonce((n) => n + 1);
    }, POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [isDelivering, refetchNotification]);

  // Email delivery stats — queried from emailDeliveries, grouped by status.
  // Only flip loading=true on the first fetch. Subsequent pollNonce-driven
  // refetches during the delivery window keep existing stats visible so the
  // panel doesn't flicker to a spinner every 400ms.
  useEffect(() => {
    if (!notificationId || !includesEmail) {
      setEmailStats(null);
      return;
    }

    let cancelled = false;
    setEmailStats((prev) => {
      if (prev === null) setLoadingEmailStats(true);
      return prev;
    });

    getDocs(
      query(
        collection(db, "emailDeliveries"),
        where("notificationId", "==", notificationId),
      ),
    )
      .then((snapshot) => {
        if (cancelled) return;
        const stats: EmailStats = {
          total: snapshot.size,
          byStatus: { ...EMPTY_EMAIL_STATS.byStatus },
        };
        snapshot.forEach((docSnap) => {
          const status = docSnap.data().status as EmailDeliveryStatus;
          if (status in stats.byStatus) {
            stats.byStatus[status] += 1;
          }
        });
        setEmailStats(stats);
      })
      .catch(() => {
        if (cancelled) return;
        setEmailStats(EMPTY_EMAIL_STATS);
      })
      .finally(() => {
        if (!cancelled) setLoadingEmailStats(false);
      });

    return () => {
      cancelled = true;
    };
  }, [notificationId, includesEmail, pollNonce]);

  // Receipt read-state stats — queried from notificationReceipts (organizer
  // read access enabled by firestore.rules; see privacy-policy.md §6).
  // Runs for every sent notification regardless of channel because every
  // recipient gets a receipt row (the in-app inbox entry), not just push
  // recipients. Only flip loading=true on first fetch — pollNonce-driven
  // refetches keep existing stats visible to avoid flicker.
  //
  // The `where("eventId", "==", eventId)` constraint is load-bearing for
  // the security rule — the receipts rule's organizer branch reads
  // `resource.data.eventId` (per-doc), and Firestore can't pre-evaluate
  // collection queries that depend on per-doc fields. Without this
  // equality the query fails with "Missing or insufficient permissions"
  // for organizers/admins; with it Firestore can substitute the known
  // eventId into the rule and verify access before reading any docs.
  useEffect(() => {
    if (!notificationId || !isSent || !eventId) {
      setReceiptStats(null);
      return;
    }

    let cancelled = false;
    setReceiptStats((prev) => {
      if (prev === null) setLoadingReceiptStats(true);
      return prev;
    });
    setReceiptStatsError(null);

    getDocs(
      query(
        collection(db, "notificationReceipts"),
        where("eventId", "==", eventId),
        where("notificationId", "==", notificationId),
      ),
    )
      .then((snapshot) => {
        if (cancelled) return;
        let read = 0;
        let unread = 0;
        snapshot.forEach((docSnap) => {
          if (docSnap.data().readAt !== null) {
            read++;
          } else {
            unread++;
          }
        });
        setReceiptStats({ total: snapshot.size, read, unread });
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        // Log to console so the underlying error (permission_denied,
        // failed_precondition, network) is visible in Sentry / devtools
        // instead of silently rendering empty stats.
        console.error("[NotificationDetail] receipt stats query failed:", err);
        setReceiptStatsError(msg);
        setReceiptStats(EMPTY_RECEIPT_STATS);
      })
      .finally(() => {
        if (!cancelled) setLoadingReceiptStats(false);
      });

    return () => {
      cancelled = true;
    };
  }, [notificationId, isSent, pollNonce, eventId]);

  // Only show the full-page spinner on the FIRST fetch (no data yet).
  // useFirestoreDocOnce flips `loadingNotification=true` on every refetch,
  // which during the 400ms polling window would cause the entire page to
  // flicker between spinner and content. Once we have a notification doc,
  // refetches happen silently underneath the rendered content.
  if (loadingNotification && !notification) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!notification) {
    return (
      <Box>
        <PageHeader title="Notification not found" />
        <Typography color="text.secondary">
          This notification may have been deleted.
        </Typography>
        <Button
          sx={{ mt: 2 }}
          onClick={() => navigate(`/events/${eventId}/notifications`)}
        >
          Back to notifications
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={notification.title}
        action={
          <Button
            onClick={() => navigate(`/events/${eventId}/notifications`)}
          >
            Back to list
          </Button>
        }
      />

      {isDelivering && (
        <Alert
          severity="info"
          icon={<CircularProgress size={20} />}
          sx={{ mb: 3 }}
        >
          Delivering notification — waiting for the server to finish fan-out.
          Stats will appear here once delivery completes.
        </Alert>
      )}

      <Paper sx={{ p: isPhone ? 2 : 3, mb: 3 }}>
        <Typography variant="overline" color="text.secondary">
          Message
        </Typography>
        <Typography
          variant="body1"
          sx={{ mt: 1, whiteSpace: "pre-wrap" }}
        >
          {notification.body}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Stack spacing={2}>
          <DetailRow label="Status">
            <Chip
              label={notification.status}
              size="small"
              color={
                notification.status === "sent"
                  ? "success"
                  : notification.status === "cancelled"
                    ? "default"
                    : "primary"
              }
            />
          </DetailRow>

          <DetailRow label="Channels">
            <Stack direction="row" spacing={1}>
              {notification.channels.includes("push") && (
                <Chip
                  icon={<Bell size={14} />}
                  label="Push notification"
                  size="small"
                  variant="outlined"
                />
              )}
              {notification.channels.includes("email") && (
                <Chip
                  icon={<Mail size={14} />}
                  label="Email"
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
          </DetailRow>

          <DetailRow label="Audience">
            <Typography variant="body2">
              {formatAudience(notification, audienceById, userById)}
            </Typography>
          </DetailRow>

          <DetailRow label="Resolved now">
            <Typography variant="body2">
              {activeRecipientCount} active, {pendingRecipients.length} pending
              sign-in
            </Typography>
          </DetailRow>

          {notification.scheduledAt && (
            <DetailRow label="Scheduled for">
              <Typography variant="body2">
                {formatTimestamp(notification.scheduledAt, timezone)}
              </Typography>
            </DetailRow>
          )}

          {notification.sentAt && (
            <DetailRow label="Sent at">
              <Typography variant="body2">
                {formatTimestamp(notification.sentAt, timezone)}
              </Typography>
            </DetailRow>
          )}

          {notification.deepLink && (
            <DetailRow label="Deep link">
              <Typography
                variant="body2"
                sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
              >
                {notification.deepLink}
              </Typography>
            </DetailRow>
          )}

          {notification.emailSubject && (
            <DetailRow label="Email subject">
              <Typography variant="body2">
                {notification.emailSubject}
              </Typography>
            </DetailRow>
          )}
        </Stack>
      </Paper>

      {/* Recipients + in-app open tracking. Always shown for any sent
          notification because every recipient gets a receipt row (the
          in-app inbox entry), regardless of channel. */}
      {isSent && !isDelivering && (
        <Paper sx={{ p: isPhone ? 2 : 3, mb: 3 }}>
          <Typography
            variant="h6"
            component="h2"
            sx={{ mb: 0.5 }}
            data-testid="notification-recipients-heading"
          >
            Recipients
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Everyone in the targeted audience receives this notification in
            their in-app inbox, whether or not their device received a push.
          </Typography>
          {loadingReceiptStats ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <Stack
                direction="row"
                spacing={isPhone ? 0 : 2}
                flexWrap="wrap"
                sx={isPhone ? PHONE_STAT_GRID : undefined}
              >
                <StatChip
                  label="People targeted"
                  value={notification.recipientCount ?? 0}
                />
                <StatChip
                  label="Opened in app"
                  value={receiptStats?.read ?? 0}
                  color="success"
                />
                <StatChip
                  label="Not yet opened"
                  value={Math.max(
                    0,
                    (notification.recipientCount ?? 0) -
                      (receiptStats?.read ?? 0),
                  )}
                />
                {(notification.recipientCount ?? 0) > 0 && (
                  <StatChip
                    label="Open rate"
                    value={`${Math.round(
                      ((receiptStats?.read ?? 0) /
                        (notification.recipientCount ?? 1)) *
                        100,
                    )}%`}
                  />
                )}
                {pendingRecipients.length > 0 && (
                  <StatChip
                    label="Pending future receipts"
                    value={pendingRecipients.length}
                    color="warning"
                  />
                )}
              </Stack>
              {receiptStatsError && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Couldn&apos;t read open-state data: {receiptStatsError}.
                  Recipient totals are still accurate; open counts may be
                  incomplete.
                </Alert>
              )}
              {pendingRecipients.length > 0 && (
                <PendingRecipientsList persons={pendingRecipients} />
              )}
            </>
          )}
        </Paper>
      )}

      {/* Planned-recipients breakdown for scheduled/draft notifications.
          Scheduled audiences resolve again at send time, so this is a
          preview rather than a fixed manifest. Surfaces the active/pending
          split so organizers can spot targeting mistakes before send. */}
      {!isSent && notification.status !== "cancelled" && (
        <Paper sx={{ p: isPhone ? 2 : 3, mb: 3 }}>
          <Typography variant="h6" component="h2" sx={{ mb: 0.5 }}>
            Planned recipients
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Scheduled notifications resolve again at send time. Active users
            receive inbox receipts immediately; pending invitees get the
            inbox item backfilled when they first sign in.
          </Typography>
          <Stack
                direction="row"
                spacing={isPhone ? 0 : 2}
                flexWrap="wrap"
                sx={isPhone ? PHONE_STAT_GRID : undefined}
              >
            <StatChip label="Active now" value={activeRecipientCount} />
            <StatChip
              label="Pending sign-in"
              value={pendingRecipients.length}
              color={pendingRecipients.length > 0 ? "warning" : undefined}
            />
            <StatChip
              label="Potential total"
              value={activeRecipientCount + pendingRecipients.length}
            />
          </Stack>
          {pendingRecipients.length > 0 && (
            <PendingRecipientsList persons={pendingRecipients} />
          )}
        </Paper>
      )}

      {/* Push delivery — counters from notification doc, written by
          sendNotification CF at fan-out. The "devices" / "recipients"
          gap is intentional UX surface: organizers need to know that a
          push not arriving on a phone is normal (signed-out user, never
          installed, push permission declined) and the recipient still
          got the in-app inbox entry. */}
      {isSent && !isDelivering && includesPush && (
        <Paper sx={{ p: isPhone ? 2 : 3, mb: 3 }}>
          <Typography variant="h6" component="h2" sx={{ mb: 0.5 }}>
            Push notification delivery
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Of the {notification.recipientCount ?? 0} people targeted, push
            notifications can only deliver to those who have the app
            installed, signed in, and notification permission granted.
            Everyone else still sees the message in their in-app inbox.
          </Typography>
          {notification.pushTokenCount === null ? (
            <Typography color="text.secondary">
              Delivery stats are still being computed.
            </Typography>
          ) : (
            <>
              <Stack
                direction="row"
                spacing={isPhone ? 0 : 2}
                flexWrap="wrap"
                sx={isPhone ? PHONE_STAT_GRID : undefined}
              >
                <StatChip
                  label="With active device"
                  value={notification.pushTokenCount}
                />
                <StatChip
                  label="Push delivered"
                  value={notification.pushSuccessCount ?? 0}
                  color="success"
                />
                {(notification.pushFailureCount ?? 0) > 0 && (
                  <StatChip
                    label="Push failed"
                    value={notification.pushFailureCount ?? 0}
                    color="error"
                  />
                )}
              </Stack>
              {(() => {
                const recipients = notification.recipientCount ?? 0;
                const devices = notification.pushTokenCount ?? 0;
                const noDeviceCount = Math.max(0, recipients - devices);
                if (noDeviceCount > 0) {
                  return (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 2 }}
                    >
                      {noDeviceCount} of {recipients}{" "}
                      {noDeviceCount === 1 ? "recipient was" : "recipients were"}{" "}
                      signed out or hadn&apos;t granted notification permission;{" "}
                      {noDeviceCount === 1 ? "they will" : "they will"} see this
                      in their inbox the next time they open the app.
                    </Typography>
                  );
                }
                return null;
              })()}
            </>
          )}
        </Paper>
      )}

      {isSent && !isDelivering && includesEmail && (
        <Paper sx={{ p: isPhone ? 2 : 3, mb: 3 }}>
          <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
            Email delivery
          </Typography>
          {loadingEmailStats ? (
            <CircularProgress size={24} />
          ) : emailStats ? (
            <Stack
                direction="row"
                spacing={isPhone ? 0 : 2}
                flexWrap="wrap"
                sx={isPhone ? PHONE_STAT_GRID : undefined}
              >
              <StatChip label="Total" value={emailStats.total} />
              <StatChip
                label="Delivered"
                value={emailStats.byStatus.delivered}
                color="success"
              />
              <StatChip label="Sent" value={emailStats.byStatus.sent} />
              <StatChip label="Queued" value={emailStats.byStatus.queued} />
              <StatChip
                label="Failed"
                value={emailStats.byStatus.failed}
                color="error"
              />
              <StatChip
                label="Bounced"
                value={emailStats.byStatus.bounced}
                color="warning"
              />
            </Stack>
          ) : (
            <Typography color="text.secondary">
              No email delivery data.
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
}

function DetailRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  const isPhone = useIsPhone();
  if (isPhone) {
    // Phone: label above value, so the value gets the full width.
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {label}
        </Typography>
        <Box sx={{ minWidth: 0 }}>{children}</Box>
      </Box>
    );
  }
  return (
    <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ minWidth: 140, pt: 0.25 }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1 }}>{children}</Box>
    </Box>
  );
}

function StatChip({
  label,
  value,
  color = "default",
}: {
  label: string;
  /** Number for raw counts, or a pre-formatted string like "71%". */
  value: number | string;
  color?: "default" | "success" | "error" | "warning";
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: 2,
        py: 1,
        minWidth: 96,
        textAlign: "center",
      }}
    >
      <Typography
        variant="h6"
        component="div"
        color={color === "default" ? "text.primary" : `${color}.main`}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Paper>
  );
}

function PendingRecipientsList({ persons }: { persons: Person[] }) {
  const isPhone = useIsPhone();
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Pending invitees
      </Typography>
      <Stack
        direction="row"
        spacing={0.75}
        flexWrap="wrap"
        useFlexGap={isPhone}
        sx={{ mt: 0.75 }}
      >
        {persons.slice(0, 12).map((person) => (
          <Chip
            key={person.id}
            label={
              person.displayName ||
              [person.firstName, person.lastName].filter(Boolean).join(" ") ||
              person.email
            }
            size="small"
            variant="outlined"
          />
        ))}
        {persons.length > 12 && (
          <Chip
            label={`+${persons.length - 12} more`}
            size="small"
            variant="outlined"
          />
        )}
      </Stack>
    </Box>
  );
}
